import { request } from '@utilities/graphQLClient';
import { gql } from 'graphql-request';
import {
  formatterPercentage,
  formatterCurrency,
  formatterCurrencyTruncated,
} from '@utilities/format';
import type { CurrencyISO } from '@/constants/currencies';

interface CurrencyInfo {
  primary_trading_item_currency_iso: string;
  primary_trading_item_currency_symbol: string;
  reporting_currency_iso: string;
  reporting_currency_symbol: string;
  reporting_unit: number;
  reporting_unit_abs: number;
  reporting_unit_text: string;
  reporting_unit_text_abs: string;
  trading_item_currency_iso: string;
  trading_item_currency_symbol: string;
}

interface Response {
  Company: {
    listingCurrencyISO: CurrencyISO;
    analysisValue: {
      lastSharePrice: number | null;
      marketCap: number | null;
      return1yAbs: number | null;
    };
    data: {
      currencyInfo: CurrencyInfo;
    };
  };
}

interface CompanyAnalysis {
  listingCurrencyISO: CurrencyISO;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
  marketCap: number | null;
  marketCapFormatted: string;
  sharePrice: number | null;
  sharePriceFormatted: string;
}

export const getDefaultAnalysis = () => ({
  listingCurrencyISO: undefined,
  return1YrAbs: null,
  return1YrAbsFormatted: '',
  marketCap: null,
  marketCapFormatted: '',
  sharePrice: null,
  sharePriceFormatted: '',
});

export async function fetchAnalysis(
  companyId: string
): Promise<CompanyAnalysis | undefined> {
  try {
    const response = await request<Response>(
      gql`
        query company($companyId: String!) {
          Company(id: $companyId) {
            listingCurrencyISO
            analysisValue {
              lastSharePrice
              marketCap
              return1yAbs
            }
            data {
              currencyInfo
            }
          }
        }
      `,
      {
        companyId,
      }
    );
    const company = response.Company;
    const { return1yAbs, marketCap, lastSharePrice } = company.analysisValue;
    const currencyISO = company.data.currencyInfo.trading_item_currency_iso;
    return {
      listingCurrencyISO: company.listingCurrencyISO,
      return1YrAbs: return1yAbs,
      return1YrAbsFormatted: formatterPercentage(return1yAbs),
      marketCap: marketCap,
      marketCapFormatted: formatterCurrencyTruncated(marketCap, currencyISO, 1),
      sharePrice: lastSharePrice,
      sharePriceFormatted: formatterCurrency(lastSharePrice, currencyISO),
    };
  } catch (error) {
    throw error;
  }
}
